import { type IconName } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { useNotifications } from 'src/core/modules/app/notifications';
import { NotificationType } from 'src/core/modules/app/notifications/notification';

import { useTriggerSurvey } from '../../event-tracking/useTriggerSurvey';
import { useCostCenterPolling } from '../../hooks/useCostCenterPolling';
import { useCreateOrganisationCostCenter } from '../../hooks/useCreateOrganisationCostCenter';
import { CommonModal } from '../CommonModal';
import {
  CostCenterAssignmentContent,
  type CostCenterAssignmentFormValues,
} from '../CostCenterAssignmentContent';
import {
  CostCenterDetailsContent,
  type CostCenterDetailsFormValues,
} from '../CostCenterDetailsContent';

export type FormValues = {
  costCenterValues: {
    name: string | undefined;
    ownerId: string | undefined;
    viewerIds: string[];
  };
  costCenterAssignmentValues: {
    companyIds: string[];
    autoAssignFutureEntities: boolean;
  };
};

type Props = {
  isOpen: boolean;
  onCancel(): void;
};

export const OrganisationSettingsModal = ({ isOpen, onCancel }: Props) => {
  const { t } = useTranslation('global');
  const [step, setStep] = useState<'details' | 'assignment'>('details');
  const { mutate: createCostCenter } = useCreateOrganisationCostCenter();
  const { pushNotif } = useNotifications();
  const triggerSurvey = useTriggerSurvey();
  const { isSubmitting, startPolling, stopPolling } = useCostCenterPolling({
    type: 'create',
    onSuccess: onCancel,
  });

  useEffect(() => {
    if (isOpen) {
      setStep('details');
      formikProps.resetForm();
    }
  }, [isOpen]);

  const formikProps = useFormik<FormValues>({
    initialValues: {
      costCenterValues: {
        name: undefined,
        ownerId: undefined,
        viewerIds: [],
      },
      costCenterAssignmentValues: {
        companyIds: [],
        autoAssignFutureEntities: false,
      },
    },
    onSubmit: async (values) => {
      try {
        startPolling();
        await createCostCenter({
          type:
            values.costCenterAssignmentValues.companyIds.length > 1
              ? 'multiEntity'
              : 'singleEntity',
          name: values.costCenterValues.name ?? '', // safe to do because we validate the form
          ownerId: values.costCenterValues.ownerId ?? '', // safe to do because we validate the form
          viewerIds: values.costCenterValues.viewerIds,
          companyIds: values.costCenterAssignmentValues.companyIds,
          autoAssignFutureEntities:
            values.costCenterAssignmentValues.autoAssignFutureEntities,
        });
        triggerSurvey();
      } catch (error) {
        stopPolling();
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'multiEntityHub.costCenters.messages.errorCreateCostCenter',
          ),
        });
        throw error;
      }
    },
  });

  const handleAssignmentComplete = (values: CostCenterAssignmentFormValues) => {
    formikProps.setFieldValue('costCenterAssignmentValues', values);
    formikProps.submitForm();
  };

  const handleDetailsComplete = (values: CostCenterDetailsFormValues) => {
    formikProps.setFieldValue('costCenterValues', values);
    setStep('assignment');
  };

  return (
    <CommonModal
      isOpen={isOpen}
      title={getModalTitle(step, t)}
      subtitle={getModalSubtitle(step, t)}
      iconName={getModalIcon(step)}
      onClose={onCancel}
    >
      {step === 'details' && (
        <CostCenterDetailsContent
          onComplete={handleDetailsComplete}
          onCancel={onCancel}
          mode="stepModal"
          setting={{
            name: formikProps.values.costCenterValues.name ?? '',
            ownerId: formikProps.values.costCenterValues.ownerId ?? '',
            viewerIds: formikProps.values.costCenterValues.viewerIds,
          }}
        />
      )}
      {step === 'assignment' && (
        <CostCenterAssignmentContent
          onComplete={handleAssignmentComplete}
          onCancel={() => {
            setStep('details');
          }}
          costCenterName={formikProps.values.costCenterValues.name}
          mode="stepModal"
          isSubmitting={isSubmitting}
        />
      )}
    </CommonModal>
  );
};

const getModalSubtitle = (
  step: 'details' | 'assignment',
  translator: TGlobalFunctionTyped,
): string | undefined => {
  if (step === 'details') {
    return undefined;
  }
  return translator('multiEntityHub.costCenters.modal.assignment.subtitle');
};

const getModalIcon = (step: 'details' | 'assignment'): IconName => {
  if (step === 'details') {
    return 'plus';
  }
  return 'building-bank';
};

const getModalTitle = (
  step: 'details' | 'assignment',
  translator: TGlobalFunctionTyped,
) => {
  if (step === 'details') {
    return translator('multiEntityHub.costCenters.modal.details.title');
  }
  return translator('multiEntityHub.costCenters.modal.assignment.title');
};
